import { apiService } from './api';

export interface AuditLog {
  id: string;
  action: string;
  entity: string;
  entityId: string;
  oldValues?: any;
  newValues?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  userId?: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface AuditLogsResponse {
  success: boolean;
  data: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class AuditService {
  private parseValues(values: any): any {
    if (!values) return null;
    if (typeof values === 'string') {
      try {
        return JSON.parse(values);
      } catch {
        return null;
      }
    }
    return values;
  }

  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    entity?: string;
    action?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AuditLogsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.entity) queryParams.append('entity', params.entity);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/audit${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get<AuditLogsResponse>(url);
  }

  async getEntityAuditLogs(entityId: string, entity: string): Promise<{ success: boolean; data: AuditLog[] }> {
    return await apiService.get<{ success: boolean; data: AuditLog[] }>(`/audit/${entityId}?entity=${entity}`);
  }

  formatUserName(user?: AuditLog['user']): string {
    if (!user) return 'Unknown User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  }

  formatEntityName(entity: string): string {
    switch (entity) {
      case 'Client': return 'Client';
      case 'GuestBlogOrder': return 'Order';
      case 'GuestBlogPlacement': return 'Placement';
      case 'Project': return 'Project';
      case 'User': return 'User';
      case 'ClientAssignment': return 'Client Assignment';
      case 'Publisher': return 'Publisher';
      case 'GuestBlogSite': return 'Guest Blog Site';
      case 'LiveLinkStatus': return 'Live Link';
      case 'Invoice': return 'Invoice';
      case 'Category': return 'Category';
      case 'ClientPlan': return 'Client Plan';
      case 'PendingGuestBlogSite': return 'Pending Site';
      default: return entity;
    }
  }

  formatActionName(action: string): string {
    switch (action) {
      case 'CREATE': return 'Created';
      case 'UPDATE': return 'Updated';
      case 'DELETE': return 'Deleted';
      case 'RESTORE': return 'Restored';
      case 'ACCEPT': return 'Accepted';
      case 'REJECT': return 'Rejected';
      case 'BULK_ACCEPT': return 'Bulk Accepted';
      default: return action;
    }
  }

  getActionColor(action: string): string {
    switch (action) {
      case 'CREATE': return '#4caf50';
      case 'UPDATE': return '#ff9800';
      case 'DELETE': return '#f44336';
      case 'RESTORE': return '#0D9488';
      case 'ACCEPT': return '#059669';
      case 'REJECT': return '#DC2626';
      case 'BULK_ACCEPT': return '#059669';
      default: return '#757575';
    }
  }

  getEntityIcon(entity: string): string {
    switch (entity) {
      case 'Client': return '👤';
      case 'GuestBlogOrder': return '📦';
      case 'GuestBlogPlacement': return '🔗';
      case 'Project': return '📁';
      case 'User': return '👥';
      case 'ClientAssignment': return '🔗';
      case 'Publisher': return '🏢';
      case 'GuestBlogSite': return '🌐';
      case 'LiveLinkStatus': return '🔍';
      case 'Invoice': return '🧾';
      case 'Category': return '🏷️';
      case 'ClientPlan': return '📋';
      case 'PendingGuestBlogSite': return '⏳';
      default: return '📄';
    }
  }

  formatActivityDescription(log: AuditLog): string {
    const entityName = this.formatEntityName(log.entity);
    const actionName = this.formatActionName(log.action);
    const userName = this.formatUserName(log.user);
    const newValues = this.parseValues(log.newValues);
    const oldValues = this.parseValues(log.oldValues);

    if (log.entity === 'Client' && log.action === 'CREATE' && newValues?.name) {
      return `${userName} created client "${newValues.name}"`;
    }
    
    if (log.entity === 'GuestBlogOrder' && log.action === 'CREATE' && newValues?.orderId) {
      return `${userName} created order ${newValues.orderId}`;
    }
    
    if (log.entity === 'GuestBlogPlacement' && log.action === 'CREATE') {
      return `${userName} created a placement`;
    }
    
    if (log.entity === 'Project' && log.action === 'CREATE' && newValues?.projectName) {
      return `${userName} created project "${newValues.projectName}"`;
    }
    
    if (log.entity === 'User' && log.action === 'CREATE' && newValues?.email) {
      return `${userName} created user ${newValues.email}`;
    }
    
    if (log.entity === 'ClientAssignment' && log.action === 'CREATE') {
      return `${userName} assigned a client to an admin user`;
    }

    if (log.action === 'RESTORE' && newValues?.label) {
      return `${userName} restored ${entityName.toLowerCase()} "${newValues.label}"`;
    }

    if (log.entity === 'Publisher' && log.action === 'CREATE' && newValues?.publisherName) {
      return `${userName} created publisher "${newValues.publisherName}"`;
    }

    if (log.entity === 'Publisher' && log.action === 'DELETE' && oldValues?.publisherName) {
      return `${userName} deleted publisher "${oldValues.publisherName}"`;
    }

    if (log.entity === 'GuestBlogSite' && log.action === 'CREATE' && newValues?.site_url) {
      return `${userName} created guest blog site ${newValues.site_url}`;
    }

    if (log.entity === 'GuestBlogSite' && log.action === 'DELETE' && oldValues?.site_url) {
      return `${userName} deleted guest blog site ${oldValues.site_url}`;
    }

    if (log.entity === 'PendingGuestBlogSite' && log.action === 'ACCEPT' && newValues?.site_url) {
      return `${userName} accepted pending site ${newValues.site_url}`;
    }

    if (log.entity === 'PendingGuestBlogSite' && log.action === 'REJECT' && newValues?.site_url) {
      return `${userName} rejected pending site ${newValues.site_url}`;
    }

    if (log.entity === 'Invoice' && log.action === 'DELETE' && oldValues?.invoiceNumber) {
      return `${userName} deleted invoice ${oldValues.invoiceNumber}`;
    }

    return `${userName} ${actionName.toLowerCase()} ${entityName.toLowerCase()}`;
  }
}

export const auditService = new AuditService();
